import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
// import { Device } from "@twilio/voice-sdk"; // Removed - not needed for dashboard
import { useAuth } from '../AuthContext';
import { ROLES } from '../utils/roleUtils';
import ProtectedRoute from './ProtectedRoute';
import { publicRoutes, getSectionComponent, getSectionConfig, defaultRoutes } from './routes';
import NavigationBar from '../NavigationBar';
import DashboardHeader from '../components/DashboardHeader';
import AppLoader from '../components/AppLoader';

// Import the new auth pages
import EmailVerifiedPage from '../pages/auth/EmailVerifiedPage';
import PasswordResetPage from '../pages/auth/PasswordResetPage';

// Root redirect component - handles authentication-based routing
function RootRoute() {
  const { user, userProfile, loading } = useAuth();

  // Show loading while authentication is being determined
  if (loading) {
    return <AppLoader message="Loading..." />;
  }

  // Check if we're coming from an email verification link
  const urlHash = window.location.hash;
  const isEmailVerification = urlHash.includes('access_token') && urlHash.includes('type=');
  
  // If coming from email verification, redirect to appropriate verification page
  if (isEmailVerification) {
    const hashParams = new URLSearchParams(urlHash.substring(1));
    const type = hashParams.get('type');
    
    if (type === 'signup') {
      return <Navigate to="/auth/verify" replace />;
    } else if (type === 'recovery') {
      return <Navigate to="/auth/reset" replace />;
    }
  }

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is authenticated but profile is still loading, show loading
  if (!userProfile) {
    return <AppLoader message="Loading user profile..." />;
  }

  // Redirect authenticated user to their role-based default dashboard
  const defaultSection = defaultRoutes[userProfile.role];
  if (defaultSection) {
    return <Navigate to={`/app/${user.id}/${defaultSection}`} replace />;
  }

  // Fallback to login if no default route found for role
  return <Navigate to="/login" replace />;
}

// Main app routes component
function AppRoutes() {
  return (
    <Routes>
      {/* Root route - handles authentication-based redirection */}
      <Route path="/" element={<RootRoute />} />

      {/* Public routes */}
      {publicRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={<route.component />}
        />
      ))}

      {/* Auth callback routes for email verification and password reset */}
      <Route path="/auth/verify" element={<EmailVerifiedPage />} />
      <Route path="/auth/reset" element={<PasswordResetPage />} />

      {/* Protected app routes */}
      <Route 
        path="/app/:userId/:section" 
        element={
          <ProtectedRoute>
            <AppSection />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/:userId/activity/:callId" 
        element={
          <ProtectedRoute>
            <AppSection />
          </ProtectedRoute>
        } 
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

// App section component with phone integration and role-based rendering
function AppSection() {
  const { userId, section } = useParams();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  // Phone-related state removed - dashboard service doesn't need call functionality
  
  // Phone-related refs and ringtone removed - not needed for dashboard

  const userRole = userProfile?.role;

  // Token ref effect removed - not needed for dashboard

  // Twilio Device setup removed - dashboard service doesn't need call functionality

  // Voice token fetching removed - dashboard service doesn't need call tokens

  // Role-based section validation and redirect
  useEffect(() => {
    if (!userRole) return;

    // Check if the current section is valid for the user's role
    const sectionConfig = getSectionConfig(section);
    
    if (!sectionConfig || !sectionConfig.allowedRoles.includes(userRole)) {
      // Redirect to user's default route
      const defaultSection = defaultRoutes[userRole];
      if (defaultSection && section !== defaultSection) {
        navigate(`/app/${userId}/${defaultSection}`, { replace: true });
        return;
      }
    }
  }, [userRole, section, userId, navigate]);

  // Render the appropriate component based on section and role
  const renderMainContent = () => {
    if (!userRole) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          fontSize: '16px',
          color: '#888'
        }}>
          Loading user profile...
        </div>
      );
    }

    const Component = getSectionComponent(section, userRole);
    
    if (!Component) {
      return <Navigate to={`/app/${userId}/${defaultRoutes[userRole]}`} replace />;
    }

    return <Component />;
  };

  // Navigation handler
  const handleNavChange = (key) => {
    window.history.replaceState(null, '', `/app/${userId}/${key}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="app">
      <DashboardHeader />
      <main className="main-content">
        {renderMainContent()}
      </main>
      <NavigationBar active={section} onChange={handleNavChange} />
    </div>
  );
}

export default AppRoutes; 